import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useThemeStore = create(
  persist(
    (set, get) => ({
      // State
      theme: 'light', // 'light', 'dark', 'system'
      primaryColor: 'blue', // Default to blue to match mobile app
      accentColor: 'blue',
      fontSize: 'medium', // 'small', 'medium', 'large'
      sidebarCollapsed: false,
      animations: true,
      compactMode: false,

      // Theme colors mapping
      colorSchemes: {
        indigo: {
          primary: '#4F46E5',
          primaryLight: '#C7D2FE',
          primaryDark: '#3730A3',
          gradient: 'from-indigo-600 to-blue-700'
        },
        blue: {
          primary: '#2563EB',
          primaryLight: '#EFF6FF',
          primaryDark: '#1E40AF',
          gradient: 'from-blue-600 to-sky-500'
        },
        green: {
          primary: '#10B981',
          primaryLight: '#D1FAE5',
          primaryDark: '#047857',
          gradient: 'from-emerald-500 to-teal-600'
        },
        purple: {
          primary: '#8B5CF6',
          primaryLight: '#EDE9FE',
          primaryDark: '#6D28D9',
          gradient: 'from-purple-600 to-indigo-700'
        },
        pink: {
          primary: '#EC4899',
          primaryLight: '#FCE7F3',
          primaryDark: '#BE185D',
          gradient: 'from-pink-500 to-rose-500'
        },
        orange: {
          primary: '#F97316',
          primaryLight: '#FFEDD5',
          primaryDark: '#C2410C',
          gradient: 'from-orange-500 to-amber-600'
        }
      },
      
      // Actions
      setTheme: (theme) => {
        set({ theme })
        get().applyTheme()
      },

      // Company brand colours ({ primary, secondary }) from getmycompanydetails. Called from App.jsx.
      // DECISION PENDING: stored but not applied, so every company stays on the HealthyCoach blue.
      // The redesign uses blue-600 classes directly; applying a brand colour only to --primary would
      // give a half-branded UI. If we turn this on, swap those classes for primary tokens first.
      brandColors: null,
      setBrandColors: ({ primary, secondary } = {}) => {
        const isHex = (v) => typeof v === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim())
        const next = isHex(primary) || isHex(secondary)
          ? { primary: isHex(primary) ? primary.trim() : null, secondary: isHex(secondary) ? secondary.trim() : null }
          : null
        set({ brandColors: next })
      },

      setPrimaryColor: (color) => {
        set({ primaryColor: color })
        get().applyTheme()
      },

      setAccentColor: (color) => {
        set({ accentColor: color })
        get().applyTheme()
      },

      setFontSize: (size) => {
        set({ fontSize: size })
        get().applyFontSize()
      },

      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed })
      },

      setAnimations: (enabled) => {
        set({ animations: enabled })
        get().applyAnimations()
      },

      setCompactMode: (enabled) => {
        set({ compactMode: enabled })
        get().applyCompactMode()
      },

      // The app uses one company-wide look (light, design-system blue).
      // Users can no longer pick a theme or color, so this clears any
      // per-user choices saved by older versions of the app.
      applyTheme: () => {
        const root = document.documentElement
        root.classList.remove('dark')
        ;['--color-primary', '--color-primary-light', '--color-primary-dark', '--primary', '--fitness-primary', '--ring']
          .forEach((name) => root.style.removeProperty(name))
        if (get().theme !== 'light' || get().primaryColor !== 'blue' || get().compactMode) {
          set({ theme: 'light', primaryColor: 'blue', accentColor: 'blue', compactMode: false })
        }
      },

      applyFontSize: () => {
        const { fontSize } = get()
        const root = document.documentElement
        
        const fontSizes = {
          small: '14px',
          medium: '16px',
          large: '18px'
        }
        
        root.style.setProperty('--base-font-size', fontSizes[fontSize])
        root.style.fontSize = fontSizes[fontSize]
      },

      applyAnimations: () => {
        const { animations } = get()
        const root = document.documentElement
        
        if (animations) {
          root.classList.remove('no-animations')
        } else {
          root.classList.add('no-animations')
        }
      },

      applyCompactMode: () => {
        const { compactMode } = get()
        const root = document.documentElement
        
        if (compactMode) {
          root.classList.add('compact-mode')
        } else {
          root.classList.remove('compact-mode')
        }
      },

      // Initialize theme on app start
      initializeTheme: () => {
        const { applyTheme, applyFontSize, applyAnimations, applyCompactMode } = get()
        applyTheme()
        applyFontSize()
        applyAnimations()
        applyCompactMode()

      },

      // Reset to defaults
      resetTheme: () => {
        set({
          theme: 'light',
          primaryColor: 'blue',
          accentColor: 'blue',
          fontSize: 'medium',
          sidebarCollapsed: false,
          animations: true,
          compactMode: false
        })
        get().initializeTheme()
      }
    }),
    {
      name: 'theme-storage',
      version: 1,
    }
  )
)